// One area of operations, as a card: the hillshade thumbnail the terrain pipeline produced, what the
// ground is like in three numbers read off the movement-cost layer, and the mission one-liner. Four of
// these are the whole of stage 1 until one is chosen (spec §2).

import type { AoMeta } from '../../sim/types';
import { missionLineFor, terrainMix, type TerrainMix } from '../opord';

export interface AoCardData {
  name: string;
  meta: AoMeta;
  /** The movement-cost layer, used only for the going summary on the card. */
  cost: Uint8Array;
}

/** The going, as three segments of one bar: colour carries the meaning, the numbers confirm it. */
const GOING: { key: keyof TerrainMix; label: string; color: string; title: string }[] = [
  { key: 'open', label: 'open', color: 'var(--color-isr)', title: 'Open going: a tracked vehicle keeps its speed (movement cost 1-5)' },
  { key: 'restricted', label: 'restricted', color: 'var(--color-warn)', title: 'Restricted going: forest, soft ground or steep slope slows movement (cost 6 and up)' },
  { key: 'nogo', label: 'no-go', color: 'var(--color-hostile)', title: 'No-go: cliff, open water or built-up core — nothing crosses it' },
];

export function AoCard({ data, disabled, onChoose }: { data: AoCardData; disabled?: boolean; onChoose: () => void }) {
  const mix = terrainMix(data.cost);
  return (
    <button
      type="button"
      data-testid={`ao-${data.name}`}
      disabled={disabled}
      onClick={onChoose}
      className="group flex w-full flex-col overflow-hidden rounded-sm border border-slate-700 bg-slate-800/60 text-left transition-colors hover:border-[var(--color-friendly)] disabled:opacity-50"
    >
      <span className="relative block h-[150px] w-full overflow-hidden bg-ink">
        {/* The pipeline's hillshade is a light-grey relief render; the filter puts it in the
            ops-center register without inverting it, which would read the ridges as valleys. */}
        <img
          src={`/ao/${data.name}/preview.png`}
          alt={`Hillshade of ${data.meta.title}`}
          className="h-full w-full object-cover transition-[filter] duration-200"
          style={{ filter: 'grayscale(1) brightness(0.5) contrast(1.25)' }}
        />
        <span
          className="pointer-events-none absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, color-mix(in srgb, var(--color-friendly) 8%, transparent), transparent 55%, color-mix(in srgb, var(--color-ink) 65%, transparent))' }}
        />
        <span className="mono absolute right-1.5 top-1.5 rounded-sm bg-ink/80 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.15em] text-fog">
          {Math.round(data.meta.sizeM / 1000)} × {Math.round(data.meta.sizeM / 1000)} km
        </span>
      </span>

      <span className="block px-3 py-2">
        <span className="block text-[14px] text-paper">{data.meta.title}</span>
        <span className="mono mt-0.5 block text-[10px] uppercase tracking-[0.15em] text-fog">
          {data.meta.lat.toFixed(2)}°, {data.meta.lon.toFixed(2)}° · {data.meta.cellM} m cells
        </span>

        <span className="mt-2 flex h-[5px] w-full overflow-hidden rounded-full">
          {GOING.map((g) => (
            <span key={g.key} title={g.title} style={{ width: `${mix[g.key]}%`, background: g.color }} />
          ))}
        </span>
        <span className="mono mt-1 flex flex-wrap gap-x-2 text-[10px] text-fog">
          {GOING.map((g) => (
            <span key={g.key} title={g.title}>
              <span style={{ color: g.color }}>{mix[g.key]}%</span> {g.label}
            </span>
          ))}
        </span>

        <span className="mt-2 block text-[11px] leading-relaxed text-fog">{missionLineFor(data.meta)}</span>
      </span>
    </button>
  );
}
